import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { useRouteMatch } from "react-router-dom";

import type { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import { Text } from "src/library/components/Text/Text";
import { getOrderDataRequired } from "src/core/drops/utils/inherited-settings";
import { useCurrentOrderValidation } from "src/context/order-validation-context";
import {
  orderPendingPlaced,
  poNumberIsValid
} from "src/core/orders/utils/orders";
import type { IStore } from "src/core/shared/store";
import { IOrder } from "src/core/orders/api/orders.api";
import { useOrder } from "src/core/orders/hooks";
import { useDrop } from "src/core/drops/hooks";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import { DataRequirement } from "src/core/drops/api/drops.api";
import { OrderDateChangePickerModal } from "src/core/orders/components/OrderDateChangePickerModal";

import { StyledInput } from "../InputFields";

interface IProps {
  terminology: ITerminology;
  setShowConvertToAdhocModal: (value: boolean) => void;
}

const OrderActionListWithPONumber: React.ReactFCWithChildren<IProps> = ({ terminology, setShowConvertToAdhocModal }) => {
  const [ poError, setPOError ] = React.useState<string | null>(null);
  const match = useRouteMatch<{ orderId?: string }>();
  const orderId = match.params.orderId ? React.useMemo(() => parseInt(match.params.orderId as string, 0), [ match ]) : null;
  const [ { drop } ] = useDrop();

  const { currentRole, orderStatuses } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    orderStatuses: global.settings && global.settings.orderStatuses
  }));

  const { canBeSubmitted, canBeSaved } = useCurrentOrderValidation();
  const orderRequiredData = drop ? getOrderDataRequired(drop.groupedSettings) : null;
  const [ showOrderDatePicker, setShowOrderDatePicker ] = React.useState(false);
  let actionButtonItemsEditMode: IAction[] = [];

  const [
    {
      currentOrder, isLoading: isLoadingOrder, isCreatingOrder,
      isSubmittingOrder,
      isSavingOrder
    },
    {
      saveOrder, setOrder, submitOrder
    }
  ] = useOrder(
    orderId,
    null,
    false
  );

  const loading = React.useMemo(() => {
    return isLoadingOrder || isCreatingOrder || isSubmittingOrder || isSavingOrder;
  }, [
    isCreatingOrder,
    isLoadingOrder,
    isSavingOrder,
    isSubmittingOrder
  ]);

  const orderAwaitingSubmission = React.useMemo(() => {
    if (currentOrder.data && orderStatuses) {
      return orderPendingPlaced(currentOrder.data, orderStatuses);
    }

    return false;
  }, [ currentOrder.data, orderStatuses ]);

  /**
   * Save to Draft button
   */
  const saveToDraftButton = React.useMemo(() => {
    if (currentOrder.data && drop && orderStatuses) {
      const draftId = getOrderStatusIdFromCode(orderStatuses, "draft");

      if (
        currentOrder.mode === "edit" &&
        !currentOrder.data.orderReference &&
        currentOrder.data.orderStatusId === draftId &&
        !orderAwaitingSubmission
      ) {
        return [
          {
            title: "Save as draft",
            action: () => {
              saveOrder();
            },
            disabled: isLoadingOrder || canBeSaved !== null
          }
        ];
      }
    }

    return [];
  }, [
    canBeSaved,
    currentOrder.data,
    currentOrder.mode,
    drop,
    isLoadingOrder,
    orderAwaitingSubmission,
    orderStatuses,
    saveOrder
  ]);

  const placeOrder = React.useCallback((orderDeliveryDate?: Date) => {
    const submitData = currentOrder.data as IOrder;

    if (!submitData.orderDeliveryDate && !orderDeliveryDate) {
      setShowOrderDatePicker(true);

      return;
    }

    if ((orderRequiredData?.orderPONumber === true || (orderRequiredData?.orderPONumber as DataRequirement)?.required) && !currentOrder.data?.customerReference) {
      return setPOError("PO Number is required");
    } else {
      // If you are editing a standing order order, warn that it will be converted to adhoc
      if ((submitData.scheduleId && currentOrder.data?.type === "scheduled") || (currentOrder.data?.type === "bulk" && currentOrder.data.origin)) {
        return setShowConvertToAdhocModal(true);
      }

      submitOrder(orderDeliveryDate);
    }
  }, [
    currentOrder.data,
    orderRequiredData?.orderPONumber,
    setShowConvertToAdhocModal,
    submitOrder
  ]);

  /**
   * Place order button
   */
  const placeOrderButton = React.useMemo(() => {
    if (currentOrder.data && drop) {
      if (currentOrder.mode === "edit") {
        let isDisabled = false;

        // If error or loading set disabled
        if (isLoadingOrder || loading || !!poError) {
          isDisabled = true;
        }

        // Only allow admin to submit orders that don't meet requirements
        if (currentRole?.type === "customer" || currentRole?.code === "account-manager") {
          if (canBeSubmitted !== null) {
            isDisabled = true;
          }
        }

        return [
          {
            title: `Place ${terminology.order.singular.text}`,
            action: placeOrder,
            disabled: isDisabled
          }
        ];
      }
    }

    return [];
  }, [
    canBeSubmitted,
    currentOrder.data,
    currentOrder.mode,
    currentRole?.code,
    currentRole?.type,
    drop,
    isLoadingOrder,
    loading,
    placeOrder,
    poError,
    terminology.order.singular.text
  ]);

  // Set action button items array
  actionButtonItemsEditMode = [ ...placeOrderButton, ...saveToDraftButton ];

  return (
    <>
      <OrderActionListWithPONumberWrapper>
        <div className="input-wrapper">
          {currentOrder.data && (
            <StyledInput
              onChange={e => {
                if (currentOrder.data) {
                  setOrder({
                    ...currentOrder,
                    data: {
                      ...currentOrder.data,
                      // dont allow the po number to be only spaces. Spaces at the start or finish will be allowed as the backend will trim the data
                      customerReference: !e.target.value.replace(/\s/g, "").length ? "" : e.target.value
                    }
                  });

                  if (drop) {
                    const poNumberError = poNumberIsValid(e.target.value, drop);

                    setPOError(poNumberError);
                  }
                }
              }}
              value={currentOrder.data.customerReference}
              placeholder={`Purchase Order No.${orderRequiredData?.orderPONumber === true || (orderRequiredData?.orderPONumber as DataRequirement)?.required ? " *" : ""}`}
            />
          )}

          {poError && (
            <div className="error-wrapper">
              <Text color="error">
                {poError}
              </Text>
            </div>
          )}
        </div>

        <div className="actions-button-wrapper">
          <ActionButton
            firstActionClickable
            title={`${terminology.order.singular.text} actions`}
            items={actionButtonItemsEditMode}
            disabled={actionButtonItemsEditMode.length === 0 || !!poError}
            loading={loading}
          />
        </div>
      </OrderActionListWithPONumberWrapper>

      {currentOrder.data && (
        <OrderDateChangePickerModal
          order={currentOrder.data}
          show={showOrderDatePicker}
          dismiss={() => setShowOrderDatePicker(false)}
          onSubmit={orderDeliveryDate => placeOrder(orderDeliveryDate)}
          isLoading={isSubmittingOrder}
        />
      )}
    </>
  );
};

export default OrderActionListWithPONumber;

const OrderActionListWithPONumberWrapper = styled.div`
  ${({ theme }) => css`
    display:flex;
    flex-wrap: wrap;

    .input-wrapper {
      min-width: 140px;
      flex-grow: 1;
      input  {
        font-size: ${theme.fonts.sizes.p};
        border: solid 1px ${theme.colors.lightGrey};
        width: 100%;
        height: auto;
        margin: 0;
        border-bottom-left-radius: ${theme.borderRadius};
        border-top-left-radius: ${theme.borderRadius};
        max-width: none;
      }
      .error-wrapper {
        background: ${theme.colors.white};
      }
    }
    .actions-button-wrapper {
      display: flex;
      flex-grow: 1;
      
      .action-button-container {
        width: 100%;
      }

      button {
        width: 100%;
        display: flex;
        justify-content: center;
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }

      .first-clickable-wrapper {
        width: 100%;

        > div:first-child {
          flex-grow: 1;      
        }
      }      
    }
  `}
`;
